import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, Zap, Shield, Globe, Video, ArrowRight } from 'lucide-react';

const features = [
  {
    icon: Sparkles,
    title: 'Multiple AI Models',
    description: 'Access Sora, Veo 3.1, Runway, and Kling AI through a unified interface.',
  },
  {
    icon: Zap,
    title: 'Edge Computing',
    description: 'Lightning-fast processing powered by Cloudflare\'s global edge network.',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-grade encryption and secure API key management.',
  },
  {
    icon: Globe,
    title: 'Global CDN',
    description: 'Instant video delivery through 300+ edge locations worldwide.',
  },
];

const providers = [
  { name: 'OpenAI Sora', quality: 'Highest', speed: 'Slow', best: 'Complex scenes' },
  { name: 'Google Veo 3.1', quality: 'High', speed: 'Medium', best: 'Balanced' },
  { name: 'Runway Gen-3', quality: 'Good', speed: 'Fast', best: 'Quick iterations' },
  { name: 'Kling AI', quality: 'Good', speed: 'Medium', best: 'Cost-effective' },
];

export default function HomePage() {
  return (
    <div className="relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-800/50 rounded-full border border-gray-700 mb-8">
              <Sparkles className="w-4 h-4 text-accent-400" />
              <span className="text-sm text-gray-300">Powered by Cloudflare Edge</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="gradient-text">AI Video Generation</span>
              <br />
              <span className="text-white">At The Edge</span>
            </h1>

            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
              Create stunning videos with Sora, Veo 3.1, Runway, and more.
              Professional-grade AI video generation with global edge delivery.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/generate" className="btn-primary flex items-center space-x-2">
                <Video className="w-5 h-5" />
                <span>Start Creating</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/register" className="btn-secondary">
                Create Free Account
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why AI Video Studio?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Built on Cloudflare's edge infrastructure for unmatched performance and reliability.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card hover:border-primary-500/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Providers Section */}
      <section className="py-20 px-4 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Multiple AI Providers
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose the best model for your needs. Smart routing automatically selects the optimal provider.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {providers.map((provider, index) => (
              <motion.div
                key={provider.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card"
              >
                <h3 className="text-lg font-semibold text-white mb-4">{provider.name}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Quality</span>
                    <span className="text-primary-400">{provider.quality}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Speed</span>
                    <span className="text-accent-400">{provider.speed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Best for</span>
                    <span className="text-gray-300">{provider.best}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="card bg-gradient-to-br from-primary-500/10 to-accent-500/10 border-primary-500/30"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Create Amazing Videos?
            </h2>
            <p className="text-gray-400 mb-8">
              Start with 10 free credits. No credit card required.
            </p>
            <Link to="/register" className="btn-primary inline-flex items-center space-x-2">
              <span>Get Started Free</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
